import { Fancybox } from '@fancyapps/ui';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const CONFIGURATOR_MEDIA_ROLES = new Set([
  'front',
  'back',
  'left',
  'right',
  'side-left',
  'side-right',
]);
const OVERLAY_PROPERTIES = [
  '--clublogo-width',
  '--clublogo-top',
  '--clublogo-left',
  '--numbers-width',
  '--numbers-top',
  '--numbers-left',
  '--numbers-rotation',
  '--name-width',
  '--name-top',
  '--name-left',
];

const normalizeMediaId = (mediaId) => String(mediaId ?? '').trim();
const MINIMUM_FLOCKING_GAP = 0.02;
const isFiniteDataNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isNormalizedDataNumber = (value) => isFiniteDataNumber(value) && value >= 0 && value <= 1;

const getNumberBounds = (layer, canvasAspectRatio) => {
  const x = Number(layer?.x);
  const y = Number(layer?.y);
  const width = Number(layer?.width);
  const rotation = Number(layer?.rotation ?? 0);
  if (![x, y, width, rotation, canvasAspectRatio].every(Number.isFinite)) return null;

  const radians = (rotation * Math.PI) / 180;
  const rotationFactor = Math.abs(Math.cos(radians)) + Math.abs(Math.sin(radians));
  const unrotatedHeight = width * canvasAspectRatio;
  const horizontalExtent = (width * rotationFactor) / 2;
  const verticalExtent = (unrotatedHeight * rotationFactor) / 2;
  const verticalCenter = y + unrotatedHeight / 2;

  return {
    bottom: verticalCenter + verticalExtent,
    left: x - horizontalExtent,
    right: x + horizontalExtent,
    top: verticalCenter - verticalExtent,
  };
};

const getOverlayLayerIssues = (layers, canvasAspectRatio) => {
  const issues = [];
  ['clubLogo', 'name'].forEach((layerName) => {
    const layer = layers?.[layerName];
    const x = Number(layer?.x);
    const y = Number(layer?.y);
    const width = Number(layer?.width);
    if (![x, y, width].every(Number.isFinite)) return;
    if (x - width / 2 < 0 || x + width / 2 > 1) {
      issues.push(`${layerName} extends beyond the horizontal canvas bounds`);
    }
    if (y < 0 || y > 1) issues.push(`${layerName} extends beyond the vertical canvas bounds`);
  });

  const numberBounds = getNumberBounds(layers?.numbers, canvasAspectRatio);
  if (numberBounds) {
    if (numberBounds.left < 0 || numberBounds.right > 1) {
      issues.push('numbers extend beyond the horizontal canvas bounds');
    }
    if (numberBounds.top < 0 || numberBounds.bottom > 1) {
      issues.push('numbers extend beyond the vertical canvas bounds');
    }
    const nameTop = Number(layers?.name?.y);
    if (Number.isFinite(nameTop) && nameTop < numberBounds.bottom + MINIMUM_FLOCKING_GAP) {
      issues.push('name and numbers layers overlap');
    }
  }

  return issues;
};

const hasInvalidRecordLayerValues = (layers) => {
  const supportedLayers = new Set(['clubLogo', 'name', 'numbers']);
  return Object.entries(layers || {}).some(([layerName, layer]) => {
    if (!supportedLayers.has(layerName) || !layer || typeof layer !== 'object') return true;
    return (
      ['x', 'y', 'width'].some((property) => {
        if (layer[property] == null) return false;
        const value = layer[property];
        return !isNormalizedDataNumber(value) || (property === 'width' && value === 0);
      }) ||
      (layer.rotation != null && !isFiniteDataNumber(layer.rotation))
    );
  });
};

const normalizedNumber = (value, fallback = 0.5) => {
  if (typeof value !== 'string' || value.trim() === '') return fallback;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 1 ? number : fallback;
};

class ProductMedia extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.eventController = new AbortController();

    this.stage = this.querySelector('[data-product-media-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-product-media-item]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-product-media-thumbnail]'));
    this.thumbnailScroller = this.querySelector('[data-product-media-thumbnails]');
    this.railPrevious = this.querySelector('[data-product-media-rail-previous]');
    this.railNext = this.querySelector('[data-product-media-rail-next]');
    this.previousButton = this.querySelector('[data-product-media-previous]');
    this.nextButton = this.querySelector('[data-product-media-next]');
    this.pagination = this.querySelector('[data-product-media-pagination]');
    this.status = this.querySelector('[data-product-media-status]');
    this.overlay = this.querySelector('[data-product-media-overlay]');
    this.mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.geometry = this.readJsonConfig(
      '[data-product-media-geometry]',
      'configurator.media_geometry',
    );
    this.presentation = this.readJsonConfig(
      '[data-product-media-presentation]',
      'gallery.media_presentation',
    );
    if (!this.fallbackOverlayProperties) {
      this.fallbackOverlayProperties = new Map(
        OVERLAY_PROPERTIES.map((property) => [property, this.style.getPropertyValue(property)]),
      );
    }

    this.applyFocalPointRecords();
    this.applyGeometryRecords();
    this.mediaById = new Map(
      this.slides.map((slide) => [normalizeMediaId(slide.dataset.mediaId), slide]),
    );
    this.selectedMediaId = normalizeMediaId(this.dataset.selectedMediaId);
    if (!this.mediaById.has(this.selectedMediaId)) {
      this.selectedMediaId = normalizeMediaId(this.slides[0]?.dataset.mediaId);
    }

    this.handleBreakpointChange = this.configureTransport.bind(this);
    this.handleThumbnailScroll = this.updateRailControls.bind(this);
    this.handleSectionLoad = this.updateAfterSectionLoad.bind(this);

    this.bindEvents();
    this.configureTransport();
    this.setupFancybox();

    this.resizeObserver = new ResizeObserver(() => this.updateSceneGeometry());
    if (this.stage) this.resizeObserver.observe(this.stage);

    this.selectMedia(this.selectedMediaId, 'initial');
    this.validateMediaContract();
  }

  disconnectedCallback() {
    this.destroySwiper();
    this.resizeObserver?.disconnect();
    this.eventController?.abort();
    window.cancelAnimationFrame(this.navigationFocusFrame);
    Fancybox.unbind(this, '[data-fancybox]');
    this.initialized = false;
  }

  get selectedMediaId() {
    return this._selectedMediaId;
  }

  set selectedMediaId(mediaId) {
    this._selectedMediaId = normalizeMediaId(mediaId);
    this.dataset.selectedMediaId = this._selectedMediaId;
  }

  get selectedMediaRole() {
    return this.getValidatedRoleForSlide(this.mediaById.get(this.selectedMediaId));
  }

  get hasAuthoritativeGeometry() {
    return this.geometry?.version === 1 && Boolean(this.geometry.media);
  }

  getRoleMatches(role) {
    return this.slides.filter((slide) => {
      if (this.hasAuthoritativeGeometry) {
        return slide.dataset.mediaRoleSource === 'geometry' && slide.dataset.mediaRole === role;
      }
      return slide.dataset.mediaRole === role;
    });
  }

  getRoleContractMatches(role) {
    if (this.hasAuthoritativeGeometry) return this.getRoleMatches(role);
    return this.slides.filter((slide) => slide.dataset.mediaRoleCandidate === role);
  }

  getValidatedRoleForSlide(slide) {
    const role =
      slide?.dataset.mediaRole ||
      (!this.hasAuthoritativeGeometry ? slide?.dataset.mediaRoleCandidate : '') ||
      '';
    if (!CONFIGURATOR_MEDIA_ROLES.has(role)) return '';
    const matches = this.getRoleMatches(role);
    return matches.length === 1 && matches[0] === slide ? role : '';
  }

  bindEvents() {
    const { signal } = this.eventController;
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener(
        'click',
        () => {
          this.selectMedia(thumbnail.dataset.mediaId, 'thumbnail');
        },
        { signal },
      );
      thumbnail.addEventListener('keydown', (event) => this.handleThumbnailKeydown(event), {
        signal,
      });
    });

    this.railPrevious?.addEventListener('click', () => this.scrollRail(-1), { signal });
    this.railNext?.addEventListener('click', () => this.scrollRail(1), { signal });
    this.previousButton?.addEventListener(
      'click',
      () => this.scheduleNavigationFocusRecovery(this.previousButton, this.nextButton),
      { signal },
    );
    this.nextButton?.addEventListener(
      'click',
      () => this.scheduleNavigationFocusRecovery(this.nextButton, this.previousButton),
      { signal },
    );
    this.thumbnailScroller?.addEventListener('scroll', this.handleThumbnailScroll, {
      passive: true,
      signal,
    });
    this.mediaQuery.addEventListener('change', this.handleBreakpointChange, { signal });

    if (window.Shopify?.designMode) {
      window.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
    }
  }

  configureTransport() {
    if (this.mediaQuery.matches || this.slides.length <= 1) {
      this.destroySwiper();
    } else {
      this.setupSwiper();
    }

    this.selectMedia(this.selectedMediaId, 'breakpoint');
    window.requestAnimationFrame(() => {
      this.updateSceneGeometry();
      this.updateRailControls();
    });
  }

  setupSwiper() {
    if (!this.stage || this.swiperInstance) return;

    const initialSlide = Math.max(
      0,
      this.slides.findIndex(
        (slide) => normalizeMediaId(slide.dataset.mediaId) === this.selectedMediaId,
      ),
    );

    this.swiperInstance = new Swiper(this.stage, {
      a11y: {
        enabled: true,
        firstSlideMessage: this.dataset.firstMessage,
        lastSlideMessage: this.dataset.lastMessage,
        nextSlideMessage: this.dataset.nextMessage,
        paginationBulletMessage: this.dataset.paginationMessage,
        prevSlideMessage: this.dataset.prevMessage,
        slideLabelMessage: `{{index}} ${this.dataset.ofLabel} {{slidesLength}}`,
      },
      initialSlide,
      modules: [Navigation, Pagination, A11y],
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.previousButton,
      },
      pagination: {
        clickable: true,
        el: this.pagination,
      },
      slidesPerView: 1,
      watchOverflow: true,
    });

    this.swiperInstance.on('slideChange', () => {
      const slide = this.slides[this.swiperInstance.activeIndex];
      if (
        slide &&
        normalizeMediaId(slide.dataset.mediaId) !== normalizeMediaId(this.selectedMediaId)
      ) {
        this.selectMedia(slide.dataset.mediaId, 'swipe');
      }
    });
    this.swiperInstance.on('paginationUpdate', () => this.updatePaginationStates());
    this.updatePaginationStates();
  }

  destroySwiper() {
    if (!this.swiperInstance) return;
    this.swiperInstance.destroy(true, true);
    this.swiperInstance = null;
  }

  selectMedia(mediaId, reason = 'programmatic') {
    const normalizedId = normalizeMediaId(mediaId);
    const selectedSlide = this.mediaById?.get(normalizedId);
    if (!selectedSlide) return false;

    const changed = normalizedId !== this.selectedMediaId;
    this.selectedMediaId = normalizedId;
    const selectedIndex = this.slides.indexOf(selectedSlide);

    this.slides.forEach((slide) => {
      const isActive = slide === selectedSlide;
      slide.dataset.active = String(isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    });

    this.thumbnails.forEach((thumbnail) => {
      const isActive = normalizeMediaId(thumbnail.dataset.mediaId) === normalizedId;
      thumbnail.dataset.active = String(isActive);
      thumbnail.setAttribute('aria-pressed', String(isActive));
      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });

    if (
      this.swiperInstance &&
      this.swiperInstance.activeIndex !== selectedIndex &&
      reason !== 'swipe'
    ) {
      this.swiperInstance.slideTo(selectedIndex);
    }

    this.updatePaginationStates(selectedIndex);

    this.moveOverlay(selectedSlide);
    this.pauseInactiveMedia(selectedSlide);
    this.scrollSelectedThumbnailIntoView(normalizedId);
    this.updateSceneGeometry();
    this.updateRailControls();

    const detail = {
      index: selectedIndex,
      label: selectedSlide.dataset.mediaLabel || '',
      mediaId: normalizedId,
      reason,
      role: this.getValidatedRoleForSlide(selectedSlide),
    };

    if (changed && reason !== 'initial' && reason !== 'breakpoint') {
      this.announceSelection(detail.label);
    }

    this.dispatchEvent(
      new CustomEvent('product-media:change', {
        bubbles: true,
        detail,
      }),
    );

    return true;
  }

  selectMediaByRole(role, reason = 'programmatic') {
    const normalizedRole = String(role ?? '')
      .trim()
      .toLowerCase();
    if (!CONFIGURATOR_MEDIA_ROLES.has(normalizedRole)) {
      this.reportRoleIssue(
        `Unsupported media role: ${normalizedRole || '(empty)'}`,
        normalizedRole,
        reason,
      );
      return false;
    }
    const matches = this.getRoleMatches(normalizedRole);

    if (matches.length !== 1) {
      const issue =
        matches.length === 0
          ? `Missing required media role: ${normalizedRole}`
          : `Ambiguous media role: ${normalizedRole}`;
      this.reportRoleIssue(issue, normalizedRole, reason);
      return false;
    }

    return this.selectMedia(matches[0].dataset.mediaId, reason);
  }

  swiperGetActiveIndex() {
    return this.slides.findIndex(
      (slide) => normalizeMediaId(slide.dataset.mediaId) === this.selectedMediaId,
    );
  }

  swiperSlideTo(index) {
    const slide = this.slides[index];
    if (slide) this.selectMedia(slide.dataset.mediaId, 'legacy-index');
  }

  handleThumbnailKeydown(event) {
    const currentIndex = this.thumbnails.indexOf(event.currentTarget);
    let targetIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        targetIndex = Math.min(this.thumbnails.length - 1, currentIndex + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        targetIndex = Math.max(0, currentIndex - 1);
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = this.thumbnails.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = this.thumbnails[targetIndex];
    target.focus({ preventScroll: true });
    this.selectMedia(target.dataset.mediaId, 'keyboard');
  }

  scrollRail(direction) {
    if (!this.thumbnailScroller) return;
    const maxScrollTop = this.thumbnailScroller.scrollHeight - this.thumbnailScroller.clientHeight;
    const atStart = this.thumbnailScroller.scrollTop <= 1;
    const atEnd = this.thumbnailScroller.scrollTop >= maxScrollTop - 1;
    if ((direction < 0 && atStart) || (direction > 0 && atEnd)) return;

    this.thumbnailScroller.scrollBy({
      behavior: 'smooth',
      top: direction * this.thumbnailScroller.clientHeight * 0.8,
    });
  }

  scrollSelectedThumbnailIntoView(mediaId) {
    if (!this.thumbnailScroller || !this.mediaQuery.matches) return;
    const thumbnail = this.thumbnails.find(
      (item) => normalizeMediaId(item.dataset.mediaId) === mediaId,
    );
    const item = thumbnail?.closest('.product-media__thumbnail-item');
    if (!thumbnail || !item) return;

    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const visibleTop = this.thumbnailScroller.scrollTop;
    const visibleBottom = visibleTop + this.thumbnailScroller.clientHeight;

    if (itemTop < visibleTop) {
      this.thumbnailScroller.scrollTo({ behavior: 'smooth', top: itemTop });
    } else if (itemBottom > visibleBottom) {
      this.thumbnailScroller.scrollTo({
        behavior: 'smooth',
        top: itemBottom - this.thumbnailScroller.clientHeight,
      });
    }
  }

  updatePaginationStates(
    selectedIndex = this.slides.indexOf(this.mediaById?.get(this.selectedMediaId)),
  ) {
    this.pagination?.querySelectorAll('.swiper-pagination-bullet').forEach((bullet, index) => {
      const isActive = index === selectedIndex;
      bullet.setAttribute('aria-pressed', String(isActive));
      if (isActive) {
        bullet.setAttribute('aria-current', 'true');
      } else {
        bullet.removeAttribute('aria-current');
      }
    });
  }

  scheduleNavigationFocusRecovery(source, fallback) {
    window.cancelAnimationFrame(this.navigationFocusFrame);
    this.navigationFocusFrame = window.requestAnimationFrame(() => {
      this.navigationFocusFrame = null;
      if (
        source?.getAttribute('aria-disabled') === 'true' &&
        fallback?.getAttribute('aria-disabled') !== 'true'
      ) {
        fallback.focus({ preventScroll: true });
      }
    });
  }

  updateRailControls() {
    if (!this.thumbnailScroller) return;
    const maxScrollTop = this.thumbnailScroller.scrollHeight - this.thumbnailScroller.clientHeight;
    const hasOverflow = maxScrollTop > 1;
    const previousHidden = !hasOverflow || this.thumbnailScroller.scrollTop <= 1;
    const nextHidden = !hasOverflow || this.thumbnailScroller.scrollTop >= maxScrollTop - 1;

    const focusedControl = [this.railPrevious, this.railNext].find(
      (control) => control === document.activeElement,
    );

    [
      [this.railPrevious, previousHidden],
      [this.railNext, nextHidden],
    ].forEach(([control, shouldHide]) => {
      if (!control) return;
      control.hidden = shouldHide;
      control.classList.toggle('hidden!', shouldHide);
      control.setAttribute('aria-disabled', String(shouldHide));
      control.tabIndex = shouldHide ? -1 : 0;
    });

    const focusedControlIsHidden =
      (focusedControl === this.railPrevious && previousHidden) ||
      (focusedControl === this.railNext && nextHidden);
    if (!focusedControlIsHidden) return;

    const oppositeControl =
      focusedControl === this.railPrevious ? this.railNext : this.railPrevious;
    const oppositeControlIsHidden =
      oppositeControl === this.railPrevious ? previousHidden : nextHidden;
    const selectedThumbnail = this.thumbnails.find(
      (thumbnail) => normalizeMediaId(thumbnail.dataset.mediaId) === this.selectedMediaId,
    );
    (oppositeControlIsHidden ? selectedThumbnail : oppositeControl)?.focus({ preventScroll: true });
  }

  updateSceneGeometry() {
    if (!this.stage) return;
    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    if (!stageWidth || !stageHeight) return;

    this.slides.forEach((slide) => {
      const sourceWidth = Number(slide.dataset.mediaWidth);
      const sourceHeight = Number(slide.dataset.mediaHeight);
      if (!sourceWidth || !sourceHeight) return;

      const fit = slide.dataset.mediaFit === 'cover' ? 'cover' : 'contain';
      const scale =
        fit === 'cover'
          ? Math.max(stageWidth / sourceWidth, stageHeight / sourceHeight)
          : Math.min(stageWidth / sourceWidth, stageHeight / sourceHeight);
      const sceneWidth = sourceWidth * scale;
      const sceneHeight = sourceHeight * scale;
      const focalX = normalizedNumber(slide.dataset.mediaFocalX);
      const focalY = normalizedNumber(slide.dataset.mediaFocalY);
      const sceneLeft =
        fit === 'cover' ? (stageWidth - sceneWidth) * focalX : (stageWidth - sceneWidth) / 2;
      const sceneTop = fit === 'cover' ? (stageHeight - sceneHeight) * focalY : 0;
      const scene = slide.querySelector('[data-product-media-scene]');
      if (!scene) return;

      scene.style.setProperty('--product-media-scene-width', `${sceneWidth}px`);
      scene.style.setProperty('--product-media-scene-height', `${sceneHeight}px`);
      scene.style.setProperty('--product-media-scene-left', `${sceneLeft}px`);
      scene.style.setProperty('--product-media-scene-top', `${sceneTop}px`);
    });
  }

  moveOverlay(selectedSlide) {
    if (!this.overlay) return;
    const scene = selectedSlide.querySelector('[data-product-media-scene]');
    if (scene && this.overlay.parentElement !== scene) scene.append(this.overlay);

    const role = this.getValidatedRoleForSlide(selectedSlide);
    this.overlay.dataset.activeRole = role;
    this.overlay.hidden = role === '';
    this.overlay.querySelectorAll('[data-configurator-preview-role]').forEach((roleElement) => {
      roleElement.hidden = roleElement.dataset.configuratorPreviewRole !== role;
    });
    const geometryRecord =
      this.geometry?.version === 1 ? this.geometry.media?.[this.selectedMediaId] : null;
    this.applyOverlayGeometry(geometryRecord, selectedSlide);
  }

  pauseInactiveMedia(selectedSlide) {
    this.slides.forEach((slide) => {
      if (slide === selectedSlide) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe.js-youtube, iframe.js-vimeo').forEach((frame) => {
        this.pauseExternalVideo(frame);
      });
      slide.querySelectorAll('model-viewer').forEach((modelViewer) => modelViewer.pause?.());
      slide.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause?.());
    });
  }

  pauseExternalVideo(frame) {
    const message = frame.classList.contains('js-youtube')
      ? JSON.stringify({ args: [], event: 'command', func: 'pauseVideo' })
      : JSON.stringify({ method: 'pause' });

    try {
      frame.contentWindow?.postMessage(message, '*');
    } catch (error) {
      console.warn('[ProductMedia] Unable to pause external video.', error);
    }
  }

  announceSelection(label) {
    if (!this.status) return;
    this.status.textContent = '';
    window.requestAnimationFrame(() => {
      this.status.textContent = `${this.dataset.selectedMessage}: ${label}`;
    });
  }

  setupFancybox() {
    Fancybox.bind(this, '[data-fancybox]', {
      animated: false,
      mainClass: 'image-zoom',
      showClass: false,
    });
  }

  readJsonConfig(selector, name) {
    const element = this.querySelector(selector);
    if (!element?.textContent.trim()) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(`[ProductMedia] Invalid ${name} JSON.`, error);
      return null;
    }
  }

  applyFocalPointRecords() {
    const presentationRecords =
      this.presentation?.version === 1 && this.presentation.media ? this.presentation.media : {};
    const geometryRecords =
      this.geometry?.version === 1 && this.geometry.media ? this.geometry.media : {};

    this.slides.forEach((slide) => {
      const mediaId = normalizeMediaId(slide.dataset.mediaId);
      [
        ['x', 'mediaFocalX', 'mediaShopifyFocalX'],
        ['y', 'mediaFocalY', 'mediaShopifyFocalY'],
      ].forEach(([axis, datasetProperty, shopifyDatasetProperty]) => {
        const configuredValue = [
          geometryRecords[mediaId]?.position?.[axis],
          presentationRecords[mediaId]?.position?.[axis],
        ].find(isNormalizedDataNumber);
        const shopifyFallback = normalizedNumber(slide.dataset[shopifyDatasetProperty]);

        slide.dataset[datasetProperty] = String(configuredValue ?? shopifyFallback);
      });
    });
  }

  applyGeometryRecords() {
    if (this.geometry?.version !== 1 || !this.geometry.media) return;

    this.slides.forEach((slide) => {
      const record = this.geometry.media[normalizeMediaId(slide.dataset.mediaId)];
      if (!record) return;

      const normalizedRole = String(record.role ?? '')
        .trim()
        .toLowerCase();
      if (CONFIGURATOR_MEDIA_ROLES.has(normalizedRole)) {
        slide.dataset.mediaRole = normalizedRole;
        slide.dataset.mediaRoleSource = 'geometry';
      } else {
        slide.dataset.mediaRole = '';
        slide.dataset.mediaRoleSource = 'none';
      }
      slide.dataset.mediaFit = record.fit === 'cover' ? 'cover' : 'contain';
      const canvasWidth = record.canvas?.width;
      const canvasHeight = record.canvas?.height;
      const sourceWidth = Number(slide.dataset.mediaSourceWidth);
      const sourceHeight = Number(slide.dataset.mediaSourceHeight);
      if (
        isFiniteDataNumber(canvasWidth) &&
        canvasWidth > 0 &&
        isFiniteDataNumber(canvasHeight) &&
        canvasHeight > 0 &&
        canvasWidth === sourceWidth &&
        canvasHeight === sourceHeight
      ) {
        slide.dataset.mediaWidth = String(canvasWidth);
        slide.dataset.mediaHeight = String(canvasHeight);
      }
    });
  }

  getFallbackOverlayLayers(slide) {
    const readPercentage = (property) =>
      Number.parseFloat(this.fallbackOverlayProperties.get(property)) / 100;
    const layers = {
      clubLogo: {
        width: readPercentage('--clublogo-width'),
        x: readPercentage('--clublogo-left'),
        y: readPercentage('--clublogo-top'),
      },
      name: {
        width: readPercentage('--name-width'),
        x: readPercentage('--name-left'),
        y: readPercentage('--name-top'),
      },
      numbers: {
        rotation: Number.parseFloat(this.fallbackOverlayProperties.get('--numbers-rotation')),
        width: readPercentage('--numbers-width'),
        x: readPercentage('--numbers-left'),
        y: readPercentage('--numbers-top'),
      },
    };
    const canvasAspectRatio =
      Number(slide?.dataset.mediaWidth) / Number(slide?.dataset.mediaHeight);
    const numberBounds = getNumberBounds(layers.numbers, canvasAspectRatio);
    if (numberBounds && Number.isFinite(layers.name.y)) {
      layers.name.y = Math.max(layers.name.y, numberBounds.bottom + MINIMUM_FLOCKING_GAP);
    }
    return layers;
  }

  resolveOverlayLayers(record, slide) {
    const fallback = this.getFallbackOverlayLayers(slide);
    const resolved = Object.fromEntries(
      Object.entries(fallback).map(([layerName, layer]) => {
        const overrides = Object.fromEntries(
          Object.entries(record?.layers?.[layerName] || {}).filter(([, value]) => value != null),
        );
        return [layerName, { ...layer, ...overrides }];
      }),
    );
    const canvasWidth = record?.canvas?.width;
    const canvasHeight = record?.canvas?.height;
    const sourceWidth = Number(slide?.dataset.mediaSourceWidth);
    const sourceHeight = Number(slide?.dataset.mediaSourceHeight);
    const recordCanvasIsValid =
      !record ||
      (isFiniteDataNumber(canvasWidth) &&
        canvasWidth > 0 &&
        isFiniteDataNumber(canvasHeight) &&
        canvasHeight > 0 &&
        canvasWidth === sourceWidth &&
        canvasHeight === sourceHeight);
    const canvasAspectRatio =
      recordCanvasIsValid && record ? canvasWidth / canvasHeight : sourceWidth / sourceHeight;
    const layerIssues = getOverlayLayerIssues(resolved, canvasAspectRatio);

    return {
      fallback,
      isValid:
        recordCanvasIsValid &&
        !hasInvalidRecordLayerValues(record?.layers) &&
        layerIssues.length === 0,
      layerIssues,
      resolved,
    };
  }

  applyOverlayGeometry(record, slide) {
    const { fallback, isValid, resolved } = this.resolveOverlayLayers(record, slide);
    const layers = isValid ? resolved : fallback;
    const layerProperties = {
      clubLogo: {
        width: '--clublogo-width',
        x: '--clublogo-left',
        y: '--clublogo-top',
      },
      name: { width: '--name-width', x: '--name-left', y: '--name-top' },
      numbers: {
        rotation: '--numbers-rotation',
        width: '--numbers-width',
        x: '--numbers-left',
        y: '--numbers-top',
      },
    };

    Object.entries(layerProperties).forEach(([layerName, properties]) => {
      const layer = layers[layerName];
      Object.entries(properties).forEach(([key, property]) => {
        const numericValue = Number(layer[key]);
        if (!Number.isFinite(numericValue)) return;
        const value = key === 'rotation' ? `${numericValue}deg` : `${numericValue * 100}%`;
        this.style.setProperty(property, value);
      });
    });
  }

  validateMediaContract() {
    const issues = [];
    const mediaIds = new Set(this.slides.map((slide) => normalizeMediaId(slide.dataset.mediaId)));

    if (this.presentation) {
      if (this.presentation.version !== 1) {
        issues.push('Unsupported media presentation version.');
      }
      const records = this.presentation.media || this.presentation;
      Object.entries(records).forEach(([mediaId, record]) => {
        if (mediaId === 'version') return;
        if (!mediaIds.has(normalizeMediaId(mediaId))) {
          issues.push(`Stale media presentation ID: ${mediaId}`);
        }
        this.validatePresentationRecord(record, `media presentation ${mediaId}`, issues);
      });
      mediaIds.forEach((mediaId) => {
        if (!records[mediaId]) issues.push(`Missing media presentation ID: ${mediaId}`);
      });
    }

    if (this.geometry) {
      if (this.geometry.version !== 1) issues.push('Unsupported media geometry version.');
      Object.entries(this.geometry.media || {}).forEach(([mediaId, record]) => {
        if (!mediaIds.has(normalizeMediaId(mediaId))) {
          issues.push(`Stale media geometry ID: ${mediaId}`);
          return;
        }

        const slide = this.mediaById.get(normalizeMediaId(mediaId));
        const canvasWidth = record?.canvas?.width;
        const canvasHeight = record?.canvas?.height;
        const sourceWidth = Number(slide?.dataset.mediaSourceWidth);
        const sourceHeight = Number(slide?.dataset.mediaSourceHeight);
        if (
          !isFiniteDataNumber(canvasWidth) ||
          canvasWidth <= 0 ||
          !isFiniteDataNumber(canvasHeight) ||
          canvasHeight <= 0
        ) {
          issues.push(`Invalid media geometry ${mediaId} canvas.`);
        } else if (
          sourceWidth &&
          sourceHeight &&
          (canvasWidth !== sourceWidth || canvasHeight !== sourceHeight)
        ) {
          issues.push(`Geometry canvas does not match base media ${mediaId}.`);
        }
        this.validatePresentationRecord(record, `media geometry ${mediaId}`, issues);
        this.validateGeometryRecord(record, mediaId, issues);
      });
    }

    ['front', 'back'].forEach((role) => {
      const candidates = this.getRoleContractMatches(role);
      const required =
        this.dataset[`requires${role[0].toUpperCase()}${role.slice(1)}Role`] === 'true';
      if (required && candidates.length === 0) issues.push(`Missing required media role: ${role}`);
      if (candidates.length > 1) issues.push(`Duplicate media role: ${role}`);
    });

    this.querySelectorAll('[data-configurator-overlay-role]').forEach((overlay) => {
      const role = overlay.dataset.configuratorOverlayRole;
      if (!CONFIGURATOR_MEDIA_ROLES.has(role)) {
        issues.push(`Unsupported configurator overlay role: ${role || '(empty)'}`);
        return;
      }
      const targets = this.getRoleContractMatches(role);
      const width = Number(overlay.dataset.configuratorOverlayWidth);
      const height = Number(overlay.dataset.configuratorOverlayHeight);
      if (targets.length === 0) {
        issues.push(`Missing ${role} media target for configurator overlay.`);
        return;
      }
      if (targets.length > 1) {
        issues.push(`Ambiguous ${role} media target for configurator overlay.`);
        return;
      }
      const [target] = targets;
      if (!(Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0)) {
        issues.push(`Invalid ${role} configurator overlay canvas.`);
        return;
      }
      if (
        width !== Number(target.dataset.mediaWidth) ||
        height !== Number(target.dataset.mediaHeight)
      ) {
        issues.push(`Overlay canvas does not match ${role} media ${target.dataset.mediaId}.`);
      }
    });

    if (issues.length) {
      this.dataset.mediaValidationErrors = JSON.stringify(issues);
      console.warn('[ProductMedia] Media contract issues:', issues);
    } else {
      delete this.dataset.mediaValidationErrors;
    }

    this.dispatchEvent(
      new CustomEvent('product-media:validation', {
        bubbles: true,
        detail: { issues },
      }),
    );
  }

  validatePresentationRecord(record, label, issues) {
    if (!record || typeof record !== 'object') {
      issues.push(`Invalid ${label} record.`);
      return;
    }
    if (record.fit != null && !['contain', 'cover'].includes(record.fit)) {
      issues.push(`Unsupported ${label} fit: ${record.fit}.`);
    }
    ['x', 'y'].forEach((axis) => {
      if (record.position?.[axis] == null) return;
      const value = record.position[axis];
      if (!isNormalizedDataNumber(value)) {
        issues.push(`Invalid ${label} position.${axis}.`);
      }
    });
  }

  validateGeometryRecord(record, mediaId, issues) {
    const role = String(record?.role ?? '')
      .trim()
      .toLowerCase();
    if (!CONFIGURATOR_MEDIA_ROLES.has(role)) {
      issues.push(`Unsupported media geometry role: ${record?.role || '(empty)'}.`);
    }

    const supportedLayers = new Set(['clubLogo', 'name', 'numbers']);
    Object.entries(record?.layers || {}).forEach(([layerName, layer]) => {
      if (!supportedLayers.has(layerName)) {
        issues.push(`Unsupported media geometry ${mediaId} layer: ${layerName}.`);
        return;
      }
      if (!layer || typeof layer !== 'object') {
        issues.push(`Invalid media geometry ${mediaId} layer: ${layerName}.`);
        return;
      }
      ['x', 'y', 'width'].forEach((property) => {
        if (layer[property] == null) return;
        const value = layer[property];
        if (!isNormalizedDataNumber(value) || (property === 'width' && value === 0)) {
          issues.push(`Invalid media geometry ${mediaId} ${layerName}.${property}.`);
        }
      });
      if (layer.rotation != null && !isFiniteDataNumber(layer.rotation)) {
        issues.push(`Invalid media geometry ${mediaId} ${layerName}.rotation.`);
      }
    });
    if (!hasInvalidRecordLayerValues(record?.layers)) {
      const slide = this.mediaById.get(normalizeMediaId(mediaId));
      this.resolveOverlayLayers(record, slide).layerIssues.forEach((issue) => {
        issues.push(`Invalid media geometry ${mediaId}: ${issue}.`);
      });
    }
  }

  reportRoleIssue(issue, role, reason) {
    console.error(`[ProductMedia] ${issue}`);
    this.dispatchEvent(
      new CustomEvent('product-media:role-error', {
        bubbles: true,
        detail: { issue, reason, role },
      }),
    );
  }

  updateAfterSectionLoad() {
    this.swiperInstance?.update();
    this.updateSceneGeometry();
    this.updateRailControls();
  }
}

window.ProductMedia = ProductMedia;

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia);
}
